/**
 * Salon utility functions for business logic
 * Reusable functions for salon-specific operations
 */

import type { FranchiseData } from './franchiseStorage';


/**
 * Check if salon/franchise is currently open
 * 
 * @param {Record<string, string>} workingHours - Working hours object
 * @param {string} timezone - Timezone (e.g., "America/Los_Angeles")
 * @returns {boolean} True if open now
 */
export const isOpenNow = (
  workingHours: Record<string, string>,
  timezone: string = 'America/New_York'
): boolean => {
  try {
    const now = new Date();
    const dayName = now
      .toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone })
      .toLowerCase();
    
    const todayHours = workingHours[dayName];
    if (!todayHours || todayHours === 'Closed') return false;
    
    // Parse hours (e.g., "9:00 AM - 8:00 PM")
    const [start, end] = todayHours.split(' - ');
    if (!start || !end) return false;
    
    const currentTime = now.toLocaleTimeString('en-US', {
      hour12: false,
      timeZone: timezone,
    });
    
    const startTime = convertTo24Hour(start);
    const endTime = convertTo24Hour(end);
    
    return currentTime >= startTime && currentTime <= endTime;
  } catch (error) {
    console.error('Error checking if open:', error);
    return false;
  }
};

/**
 * Convert 12-hour time to 24-hour format
 * 
 * @param {string} time12h - Time in 12-hour format (e.g., "9:00 AM")
 * @returns {string} Time in 24-hour format (e.g., "09:00")
 */
const convertTo24Hour = (time12h: string): string => {
  const [time, modifier] = time12h.trim().split(' ');
  let [hours, minutes] = time.split(':');
  
  if (hours === '12') {
    hours = '00';
  }
  
  if (modifier === 'PM') {
    hours = String(parseInt(hours, 10) + 12);
  }
  
  return `${hours.padStart(2, '0')}:${minutes}`;
};

/**
 * Get today's working hours
 * 
 * @param {Record<string, string>} workingHours - Working hours object
 * @param {string} timezone - Timezone
 * @returns {string} Today's hours or "Closed"
 */
export const getTodayHours = (
  workingHours: Record<string, string>,
  timezone: string = 'America/New_York'
): string => {
  const now = new Date();
  const dayName = now
    .toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone })
    .toLowerCase();
  
  return workingHours[dayName] || 'Closed';
};

/**
 * Parse features string to array
 * 
 * @param {string} features - Comma-separated features
 * @returns {string[]} Array of features
 * 
 * @example
 * parseFeatures("haircut,coloring,spa")
 * // Returns: ["haircut", "coloring", "spa"]
 */
export const parseFeatures = (features: string): string[] => {
  return features.split(',').map((f) => f.trim()).filter(Boolean);
};

/**
 * Format features for display
 * 
 * @param {string} features - Comma-separated features
 * @returns {string[]} Formatted features
 * 
 * @example
 * formatFeatures("haircut,coloring,spa")
 * // Returns: ["Haircut", "Coloring", "Spa"]
 */
export const formatFeatures = (features: string): string[] => {
  return parseFeatures(features).map((feature) => {
    return feature
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  });
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 * 
 * @param {number} lat1 - Latitude 1
 * @param {number} lon1 - Longitude 1
 * @param {number} lat2 - Latitude 2
 * @param {number} lon2 - Longitude 2
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Convert degrees to radians
 */
const toRad = (degrees: number): number => {
  return (degrees * Math.PI) / 180;
};

/**
 * Sort franchises by distance from user location
 * 
 * @param {FranchiseData[]} franchises - Array of franchises
 * @param {number} userLat - User latitude
 * @param {number} userLon - User longitude
 * @returns {FranchiseData[]} Sorted franchises
 */
export const sortFranchisesByDistance = (
  franchises: FranchiseData[],
  userLat: number,
  userLon: number
): FranchiseData[] => {
  return [...franchises].sort((a, b) => {
    const distA = calculateDistance(
      userLat,
      userLon,
      parseFloat(a.lat),
      parseFloat(a.lng)
    );
    const distB = calculateDistance(
      userLat,
      userLon,
      parseFloat(b.lat),
      parseFloat(b.lng)
    );
    return distA - distB;
  });
};

/**
 * Format phone number for display
 * 
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 * 
 * @example
 * formatPhoneNumber("+15551234567")
 * // Returns: "+1 (555) 123-4567"
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // US number with country code
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 10) {
    // US number without country code
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Return original if format not recognized
  return phone;
};

/**
 * Get franchise status badge
 * 
 * @param {FranchiseData} franchise - Franchise data
 * @returns {{ text: string; color: string }} Status badge info
 */
export const getFranchiseStatus = (
  franchise: FranchiseData
): { text: string; color: string } => {
  if (franchise.is_enabled !== 'true') {
    return { text: 'Closed', color: 'red' };
  }
  
  if (isOpenNow(franchise.working_hours, franchise.timezone)) {
    return { text: 'Open Now', color: 'green' };
  }
  
  return { text: 'Closed', color: 'gray' };
};
